/**
 * Alert Sync Hook
 *
 * Synchronizes price alerts between localStorage and Supabase.
 * Uses the existing /api/v1/alerts endpoint for database operations.
 *
 * - Authenticated users: sync to database
 * - Non-authenticated users: localStorage only (via alertService)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase/client';
import { useAuthStore } from '../store/authStore';
import { StorageService } from '../services/storage';
import type { PriceAlert } from '../services/alertService';

// Storage key for localStorage
const ALERTS_STORAGE_KEY = 'price_alerts';

// Map local condition to DB alert_type
function conditionToAlertType(condition: PriceAlert['condition'], changePercent?: number): string {
  if (condition === 'above') return 'price_above';
  if (condition === 'below') return 'price_below';
  if (condition === 'change_percent') {
    return (changePercent && changePercent > 0) ? 'price_change_up' : 'price_change_down';
  }
  // Handle portfolio and ATH alert types
  if (condition === 'portfolio_change') return 'portfolio_change_percent';
  if (condition === 'portfolio_ath') return 'portfolio_ath';
  if (condition === 'token_ath') return 'token_ath';
  return 'price_above';
}

// Map DB alert_type to local condition
function alertTypeToCondition(alertType: string): PriceAlert['condition'] {
  if (alertType === 'price_above') return 'above';
  if (alertType === 'price_below') return 'below';
  if (alertType === 'price_change_up' || alertType === 'price_change_down') return 'change_percent';
  // Handle portfolio and ATH alert types
  if (alertType === 'portfolio_change_percent') return 'portfolio_change' as PriceAlert['condition'];
  if (alertType === 'portfolio_ath') return 'portfolio_ath' as PriceAlert['condition'];
  if (alertType === 'token_ath') return 'token_ath' as PriceAlert['condition'];
  return 'above';
}

// Convert local alert to DB format
function localToDb(alert: PriceAlert, userId: string): any {
  const alertType = conditionToAlertType(alert.condition, alert.changePercent);
  const isPortfolioAlert = alert.tokenId === 'portfolio' || alertType.startsWith('portfolio_');

  return {
    user_id: userId,
    coin_id: isPortfolioAlert ? null : alert.tokenId,
    symbol: isPortfolioAlert ? null : (alert.tokenName.split(' ')[0]?.toUpperCase() || alert.tokenId.toUpperCase()),
    coin_name: alert.tokenName,
    alert_type: alertType,
    target_value: alert.targetPrice ?? Math.abs(alert.changePercent ?? 0),
    current_price: alert.basePrice ?? null,
    is_active: alert.enabled,
    repeat_notification: alert.repeatable ?? false,
    note: null,
    // Portfolio-specific fields
    alert_scope: isPortfolioAlert ? 'portfolio' : 'token',
    threshold_direction: (alert as any).thresholdDirection || 'both',
    portfolio_id: null, // Will be set to user's default portfolio if needed
  };
}

// Convert DB alert to local format
function dbToLocal(dbAlert: any): PriceAlert {
  const condition = alertTypeToCondition(dbAlert.alert_type);
  const isChangePercent = condition === 'change_percent';

  return {
    id: dbAlert.id,
    tokenId: dbAlert.coin_id,
    tokenName: dbAlert.coin_name || dbAlert.symbol,
    condition,
    targetPrice: isChangePercent ? undefined : dbAlert.target_value,
    changePercent: isChangePercent
      ? (dbAlert.alert_type === 'price_change_down' ? -dbAlert.target_value : dbAlert.target_value)
      : undefined,
    basePrice: dbAlert.current_price ?? undefined,
    enabled: dbAlert.is_active,
    repeatable: dbAlert.repeat_notification,
    createdAt: dbAlert.created_at,
    triggeredAt: dbAlert.last_triggered_at ?? undefined,
    notificationSent: !!dbAlert.last_triggered_at,
  };
}

interface UseAlertSyncReturn {
  alerts: PriceAlert[];
  isLoading: boolean;
  isSyncing: boolean;
  syncError: string | null;
  lastSyncedAt: Date | null;
  createAlert: (alert: Omit<PriceAlert, 'id' | 'createdAt'>) => Promise<PriceAlert | null>;
  updateAlert: (id: string, updates: Partial<PriceAlert>) => Promise<boolean>;
  deleteAlert: (id: string) => Promise<boolean>;
  toggleAlert: (id: string) => Promise<boolean>;
  clearTriggeredAlerts: () => Promise<void>;
  refreshAlerts: () => Promise<void>;
}

export function useAlertSync(): UseAlertSyncReturn {
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [alerts, setAlerts] = useState<PriceAlert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  // Track if initial load has been done
  const initialLoadDone = useRef(false);
  // Track if we've loaded from localStorage
  const localStorageLoaded = useRef(false);

  // Load alerts from Supabase on mount (if authenticated)
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    if (isAuthenticated && user?.id && !initialLoadDone.current) {
      initialLoadDone.current = true;
      loadFromSupabase();
    } else if (!isAuthenticated) {
      // Not authenticated - load from localStorage only
      if (!localStorageLoaded.current) {
        localStorageLoaded.current = true;
        const localAlerts = StorageService.get<PriceAlert[]>(ALERTS_STORAGE_KEY, []) || [];
        setAlerts(localAlerts);
      }
      setIsLoading(false);
    } else if (isAuthenticated && !user?.id) {
      // Auth is done loading but user.id is missing - shouldn't happen, but handle it
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.id, authLoading]);

  // Load alerts from Supabase
  const loadFromSupabase = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    setSyncError(null);

    try {
      const { data: dbAlerts, error } = await supabase
        .from('portfolio_price_alerts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (dbAlerts && dbAlerts.length > 0) {
        // Convert DB alerts to local format - DB is source of truth
        const localAlerts = dbAlerts.map(dbToLocal);
        setAlerts(localAlerts);
        // Update localStorage to match DB (clears stale data)
        StorageService.set(ALERTS_STORAGE_KEY, localAlerts);
      } else {
        // No DB data - clear any stale localStorage and set empty array
        setAlerts([]);
        StorageService.set(ALERTS_STORAGE_KEY, []);
      }

      setLastSyncedAt(new Date());
    } catch (error: any) {
      console.error('[useAlertSync] Failed to load alerts from Supabase:', error);
      setSyncError(error.message || 'Failed to load alerts');
      // On error, try to show localStorage data as fallback
      const localAlerts = StorageService.get<PriceAlert[]>(ALERTS_STORAGE_KEY, []) || [];
      setAlerts(localAlerts);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Sync local alerts to database
  const syncLocalToDb = useCallback(async (localAlerts: PriceAlert[]) => {
    if (!user?.id || !isAuthenticated) return;

    setIsSyncing(true);

    try {
      for (const alert of localAlerts) {
        const dbAlert = localToDb(alert, user.id);

        const { error } = await supabase
          .from('portfolio_price_alerts')
          .insert(dbAlert);

        if (error) {
          console.error('Error syncing alert to DB:', error);
        }
      }
    } catch (error: any) {
      console.error('Failed to sync alerts to DB:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [user?.id, isAuthenticated]);

  // Create a new alert
  const createAlert = useCallback(async (alertData: Omit<PriceAlert, 'id' | 'createdAt'>): Promise<PriceAlert | null> => {

    // For authenticated users, save to DB first, then update local state
    if (isAuthenticated && user?.id) {
      setIsSyncing(true);
      try {
        const tempAlert: PriceAlert = {
          ...alertData,
          id: crypto.randomUUID(), // Will be replaced by DB ID
          createdAt: new Date().toISOString(),
          enabled: true,
        };

        const { data, error } = await supabase
          .from('portfolio_price_alerts')
          .insert(localToDb(tempAlert, user.id))
          .select()
          .single();

        if (error) throw error;

        if (data) {
          // Use DB-generated alert (with correct DB ID)
          const dbAlert = dbToLocal(data);
          const updatedAlerts = [...alerts, dbAlert];
          setAlerts(updatedAlerts);
          StorageService.set(ALERTS_STORAGE_KEY, updatedAlerts);
          return dbAlert;
        }

        throw new Error('No data returned from insert');
      } catch (error: any) {
        console.error('[useAlertSync] Failed to create alert in DB:', error);
        setSyncError(error.message);
        return null;
      } finally {
        setIsSyncing(false);
      }
    }

    // For non-authenticated users, save to localStorage only
    const newAlert: PriceAlert = {
      ...alertData,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      enabled: true,
    };

    const updatedAlerts = [...alerts, newAlert];
    setAlerts(updatedAlerts);
    StorageService.set(ALERTS_STORAGE_KEY, updatedAlerts);

    return newAlert;
  }, [alerts, isAuthenticated, user?.id]);

  // Update an alert
  const updateAlert = useCallback(async (id: string, updates: Partial<PriceAlert>): Promise<boolean> => {
    // Update local state
    const updatedAlerts = alerts.map(a =>
      a.id === id ? { ...a, ...updates } : a
    );
    setAlerts(updatedAlerts);
    StorageService.set(ALERTS_STORAGE_KEY, updatedAlerts);

    // Sync to Supabase
    if (isAuthenticated && user?.id) {
      setIsSyncing(true);
      try {
        const dbUpdates: any = {};
        if (updates.enabled !== undefined) dbUpdates.is_active = updates.enabled;
        if (updates.targetPrice !== undefined) dbUpdates.target_value = updates.targetPrice;
        if (updates.repeatable !== undefined) dbUpdates.repeat_notification = updates.repeatable;
        if (updates.triggeredAt !== undefined) dbUpdates.last_triggered_at = updates.triggeredAt;

        const { error } = await supabase
          .from('portfolio_price_alerts')
          .update(dbUpdates)
          .eq('id', id)
          .eq('user_id', user.id);

        if (error) throw error;
        return true;
      } catch (error: any) {
        console.error('Failed to update alert in DB:', error);
        setSyncError(error.message);
        return false;
      } finally {
        setIsSyncing(false);
      }
    }

    return true;
  }, [alerts, isAuthenticated, user?.id]);

  // Delete an alert
  const deleteAlert = useCallback(async (id: string): Promise<boolean> => {

    // Update local state optimistically
    const updatedAlerts = alerts.filter(a => a.id !== id);
    setAlerts(updatedAlerts);
    StorageService.set(ALERTS_STORAGE_KEY, updatedAlerts);

    // Sync to Supabase
    if (isAuthenticated && user?.id) {
      setIsSyncing(true);
      try {
        // Use select() to verify rows were deleted
        const { data, error } = await supabase
          .from('portfolio_price_alerts')
          .delete()
          .eq('id', id)
          .eq('user_id', user.id)
          .select();

        if (error) throw error;

        return true;
      } catch (error: any) {
        console.error('[useAlertSync] Failed to delete alert in DB:', error);
        setSyncError(error.message);
        // Revert local state on error
        setAlerts(alerts);
        StorageService.set(ALERTS_STORAGE_KEY, alerts);
        return false;
      } finally {
        setIsSyncing(false);
      }
    }

    return true;
  }, [alerts, isAuthenticated, user?.id]);

  // Toggle alert enabled state
  const toggleAlert = useCallback(async (id: string): Promise<boolean> => {
    const alert = alerts.find(a => a.id === id);
    if (!alert) return false;

    const newEnabled = !alert.enabled;

    // Update local state
    const updatedAlerts = alerts.map(a =>
      a.id === id ? { ...a, enabled: newEnabled, notificationSent: newEnabled ? false : a.notificationSent } : a
    );
    setAlerts(updatedAlerts);
    StorageService.set(ALERTS_STORAGE_KEY, updatedAlerts);

    // Sync to Supabase
    if (isAuthenticated && user?.id) {
      setIsSyncing(true);
      try {
        const updates: any = { is_active: newEnabled };
        // Reset triggered status when re-enabling
        if (newEnabled) {
          updates.last_triggered_at = null;
        }

        const { error } = await supabase
          .from('portfolio_price_alerts')
          .update(updates)
          .eq('id', id)
          .eq('user_id', user.id);

        if (error) throw error;
        return true;
      } catch (error: any) {
        console.error('Failed to toggle alert in DB:', error);
        setSyncError(error.message);
        return false;
      } finally {
        setIsSyncing(false);
      }
    }

    return true;
  }, [alerts, isAuthenticated, user?.id]);

  // Clear triggered alerts
  const clearTriggeredAlerts = useCallback(async (): Promise<void> => {
    // Update local state
    const updatedAlerts = alerts.filter(a => {
      if (!a.triggeredAt) return true;

      if (a.repeatable) {
        // Reset repeatable alerts
        a.enabled = true;
        a.triggeredAt = undefined;
        a.notificationSent = false;
        a.cooldownUntil = undefined;
        return true;
      }

      // Remove non-repeatable triggered alerts
      return false;
    });
    setAlerts(updatedAlerts);
    StorageService.set(ALERTS_STORAGE_KEY, updatedAlerts);

    // Sync to Supabase
    if (isAuthenticated && user?.id) {
      setIsSyncing(true);
      try {
        // Delete non-repeatable triggered alerts
        const { error: deleteError } = await supabase
          .from('portfolio_price_alerts')
          .delete()
          .eq('user_id', user.id)
          .eq('repeat_notification', false)
          .not('last_triggered_at', 'is', null);

        if (deleteError) console.error('Error deleting triggered alerts:', deleteError);

        // Reset repeatable triggered alerts
        const { error: updateError } = await supabase
          .from('portfolio_price_alerts')
          .update({
            is_active: true,
            last_triggered_at: null,
          })
          .eq('user_id', user.id)
          .eq('repeat_notification', true)
          .not('last_triggered_at', 'is', null);

        if (updateError) console.error('Error resetting repeatable alerts:', updateError);
      } catch (error: any) {
        console.error('Failed to clear triggered alerts in DB:', error);
        setSyncError(error.message);
      } finally {
        setIsSyncing(false);
      }
    }
  }, [alerts, isAuthenticated, user?.id]);

  // Refresh alerts from database
  const refreshAlerts = useCallback(async (): Promise<void> => {
    if (isAuthenticated && user?.id) {
      await loadFromSupabase();
    }
  }, [isAuthenticated, user?.id, loadFromSupabase]);

  return {
    alerts,
    isLoading,
    isSyncing,
    syncError,
    lastSyncedAt,
    createAlert,
    updateAlert,
    deleteAlert,
    toggleAlert,
    clearTriggeredAlerts,
    refreshAlerts,
  };
}
